import { Button, PasswordInput } from "@/components/ui";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form } from "@/components/ui/form";
import { useResetPassword } from "@/api/mutation";
import { ResetPasswordDTO } from "@/api/types";

const resetPasswordSchema = z
  .object({
    password: z.string().min(6, "Password must be at least 6 characters"),
    verifyPassword: z.string().min(6, "Please verify your password"),
  })
  .refine((data) => data.password === data.verifyPassword, {
    path: ["verifyPassword"],
    message: "Passwords must match",
  });

type FormData = z.infer<typeof resetPasswordSchema>;

export const ResetPassword = () => {
  const form = useForm<FormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      verifyPassword: "",
    },
  });

  const { mutate, isPending } = useResetPassword();

  const onSubmit = async (formData: FormData) => {
    try {
      const email = localStorage.getItem("pendingVerificationEmail");

      if (!email) {
        console.error("No email found for password reset");
        return;
      }

      const resetData: ResetPasswordDTO = {
        email,
        newPassword: formData.password,
        confirmPassword: formData.verifyPassword,
      };

      mutate(resetData);
    } catch (error) {
      console.error("Reset Password error:", error);
    }
  };

  return (
    <div className='grid gap-6'>
      <header className='space-y-px'>
        <h3>Reset your password</h3>
        <p className='text-muted-foreground text-sm'>Enter your new password below.</p>
      </header>
      <Form.Root {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='grid gap-4' noValidate>
          <Form.Field
            control={form.control}
            name='password'
            render={({ field }) => (
              <Form.Item>
                <Form.Label>New Password</Form.Label>
                <Form.Control>
                  <PasswordInput placeholder='Your new password' {...field} />
                </Form.Control>
                <Form.Message />
              </Form.Item>
            )}
          />

          <Form.Field
            control={form.control}
            name='verifyPassword'
            render={({ field }) => (
              <Form.Item>
                <Form.Label>Confirm New Password</Form.Label>
                <Form.Control>
                  <PasswordInput placeholder='Confirm your new password' {...field} />
                </Form.Control>
                <Form.Message />
              </Form.Item>
            )}
          />
          <div className='flex flex-col gap-3'>
            <Button type='submit' size='lg' className='w-full' loading={isPending}>
              Reset Password
            </Button>
          </div>
        </form>
      </Form.Root>
      <div className='text-center text-sm'>
        <p>
          Remember your password? <Link to='/login'>Sign in</Link>
        </p>
      </div>
    </div>
  );
};
